import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { EMPTY, catchError, finalize, take, tap } from 'rxjs';
import { CommunityProfileModel } from 'src/app/core/models/community-profile';
import { GradeModel } from 'src/app/core/models/grade.model';
import { SegmentModel } from 'src/app/core/models/segment.model';
import { ApiLivRequestsService } from 'src/app/core/services/api/requests/api-liv-requests.service';
import { KibanaService } from 'src/app/core/services/api/requests/kibana.service';
import { UserProfileService } from 'src/app/core/services/auth/user-profile.service';
import { CapitalizeUtils } from 'src/app/shared/utils/capitalize.utils';
import { FormValidators } from 'src/app/shared/validators/form-validators';
import { MODAL_DATA } from 'src/app/ui/components/modal/modal.component';
import { ModalService } from 'src/app/ui/components/modal/modal.service';
import { SwiperContainer } from 'swiper/element';
import { Swiper, SwiperOptions } from 'swiper/types';

type ProfilesForm = FormArray<
  FormGroup<{
    checked: FormControl<boolean | null>;
    value: FormControl<number | null>;
  }>
>;

type GradesForm = FormArray<
  FormGroup<
    Record<
      string,
      FormGroup<{
        checked: FormControl<boolean | null>;
        value: FormControl<number | null>;
      }>
    >
  >
>;

interface FormGroupControl {
  profiles: ProfilesForm;
  grades: GradesForm;
  phone: FormControl<string | null>;
}

export interface OnboardingModalComponentData {
  segments: SegmentModel[];
  communityProfiles: CommunityProfileModel[];
  whatsappChannel: string;
}

export enum OnboardingSlide {
  Welcome = 0,
  Profile = 1,
  Grades = 2,
  Contact = 3,
  Whatsapp = 4
}

@Component({
  selector: 'liv-onboarding-modal',
  templateUrl: './onboarding-modal.component.html',
  styleUrl: './onboarding-modal.component.scss',
  standalone: false
})
export class OnboardingModalComponent implements OnInit, OnDestroy {
  readonly segments = signal<SegmentModel[]>([]);

  readonly communityProfiles = signal<CommunityProfileModel[]>([]);

  readonly whatsappChannel = signal<string>('');

  @ViewChild('sliderRef')
  sliderRef: ElementRef<SwiperContainer>;

  form: FormGroup<FormGroupControl>;

  private readonly activeSlideIndex = signal(OnboardingSlide.Welcome);

  readonly isWelcomeSlideActive = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Welcome
  );

  readonly isProfileSlideActive = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Profile
  );

  readonly isGradesSlideActive = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Grades
  );

  readonly isContactSlideActive = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Contact
  );

  readonly isWhatsappSlideActive = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Whatsapp
  );

  readonly isFirstSlide = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Welcome
  );

  readonly isLastSlide = computed(
    () => this.activeSlideIndex() === OnboardingSlide.Whatsapp
  );

  protected readonly updateUserRegistration = signal(false);
  protected isSavingSeeLater = false;

  constructor(
    @Inject(MODAL_DATA) private data: OnboardingModalComponentData,
    private modal: ModalService,
    private formBuilder: FormBuilder,
    private userProfileService: UserProfileService,
    private apiLivRequestsService: ApiLivRequestsService,
    private kibanaService: KibanaService,
    private toastService: ToastController,
    private language: I18nService
  ) {
    if (this.data) {
      this.segments.set(this.data.segments);
      this.communityProfiles.set(this.data.communityProfiles);
      this.whatsappChannel.set(this.data.whatsappChannel);
    }
    setTimeout(() => {
      this.buildSlider();
    });
  }

  get username(): string {
    const { nome } = this.userProfileService.profileSnapshot;
    const [rawUsername] = nome.split(' ');

    return CapitalizeUtils.capitalize(rawUsername);
  }

  get profiles(): ProfilesForm {
    return this.form.controls.profiles;
  }

  get grades(): GradesForm {
    return this.form.controls.grades;
  }

  get phone(): FormControl<string> {
    return this.form.controls.phone;
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
  }

  handleNextStep(): boolean {
    switch (this.activeSlideIndex()) {
      case OnboardingSlide.Profile:
        return this.profiles.valid;
      case OnboardingSlide.Grades:
        return this.grades.valid;
      case OnboardingSlide.Contact:
        return this.phone.valid;
      default:
        return true;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  handleSubmit(): void {
    this.updateUserRegistration.set(true);

    const profiles = this.profiles.value
      .filter((profile) => profile.checked)
      .map((profile) => profile.value);
    const grades = this.grades.value
      .flatMap((gradeGroup) => Object.values(gradeGroup))
      .filter((grade) => grade.checked)
      .map((grade) => grade.value);

    this.apiLivRequestsService
      .updateUserRegistration({
        phone: this.phone.value,
        profiles,
        grades
      })
      .pipe(
        take(1),
        finalize(() => {
          this.updateUserRegistration.set(false);
        }),
        tap(() => {
          this.kibanaService
            .saveOnboardingData({
              communityProfiles: profiles.map((profile) => {
                const { nome } = this.communityProfiles().find(
                  (communityProfile) => communityProfile.id === profile
                );
                return nome;
              }),
              grades
            })
            .pipe(take(1))
            .subscribe();
        }),
        catchError(() => {
          this.toastService.danger({
            message: this.language.instant('messages.save_data_error')
          });
          this.modal.close();
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.modal.close();
      });
  }

  handleSeeLater(): void {
    if (this.isSavingSeeLater) {
      return;
    }

    this.isSavingSeeLater = true;

    this.kibanaService
      .saveSurveySeeLater('onboarding')
      .pipe(
        take(1),
        finalize(() => (this.isSavingSeeLater = false))
      )
      .subscribe(() => {
        this.modal.close();
      });
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      profiles: this.buildProfilesFormArray(),
      grades: this.buildGradesFormArray(),
      phone: ['', [FormValidators.phone({ isRequired: false })]]
    });
  }

  private buildProfilesFormArray(): ProfilesForm {
    return this.formBuilder.array(
      this.communityProfiles().map((profile) => {
        return this.formBuilder.group({
          checked: [false],
          value: [profile.id]
        });
      }),
      FormValidators.atLeastOneCheckboxCheckedValidator(1, ['boolean'])
    );
  }

  private buildGradesFormArray(): GradesForm {
    return this.formBuilder.array(
      this.segments().map(({ anos }) => this.buildGradesFormGroup(anos)),
      FormValidators.atLeastOneCheckboxCheckedValidator(1, ['boolean'])
    );
  }

  private buildGradesFormGroup(grades: GradeModel[]) {
    return grades.reduce((formGroup, grade) => {
      formGroup.addControl(
        `${grade.id}`,
        this.formBuilder.group({
          checked: [false],
          value: [grade.id_interno]
        })
      );

      return formGroup;
    }, this.formBuilder.group({}));
  }

  slideToPrevious(): void {
    this.swiper?.slidePrev();
  }

  slideToNext(): void {
    this.swiper?.slideNext();
  }

  private get swiper(): Swiper | undefined {
    return this.sliderRef?.nativeElement?.swiper;
  }

  private syncActiveSlide(swiper: Swiper): void {
    const slide = swiper.activeIndex as OnboardingSlide;

    switch (slide) {
      case OnboardingSlide.Profile:
      case OnboardingSlide.Grades:
      case OnboardingSlide.Contact:
      case OnboardingSlide.Whatsapp:
        this.activeSlideIndex.set(slide);
        break;
      default:
        this.activeSlideIndex.set(OnboardingSlide.Welcome);
    }
  }

  private buildSlider() {
    const swiperOptions: SwiperOptions = {
      allowTouchMove: false,
      simulateTouch: false,
      keyboard: { enabled: false },
      on: {
        init: (swiper) => this.syncActiveSlide(swiper),
        slideChange: (swiper) => this.syncActiveSlide(swiper)
      },
      pagination: {
        enabled: true,
        clickable: false
      }
    };

    Object.assign(this.sliderRef.nativeElement, swiperOptions);

    this.sliderRef.nativeElement.initialize();
  }
}
